import { LockOutlined } from '@ant-design/icons';
import { Divider, Flex, Select, SelectProps, Spin } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import React, {
    forwardRef,
    Suspense,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { GraphQLTaggedNode, useRelayEnvironment } from 'react-relay';
import { Environment } from 'relay-runtime';

import SuspenseList from '../../components/SuspenseList';
import { combineFilterFlexible, getObjectFromRelayStoreById } from '../../functions';
import useDispatchSetterStore from '../../hooks/useDispatchSetterStore';
import useDynamicSelector from '../../hooks/useDynamicSelector';
import { SmxPropsContext } from '../../providers/propsProvider';
import { getSetterDynamicListItem, IDynamicListState } from '../../store/slices/dynamicListSlice';
import DataLoader from '../../table/DataLoader';
import { DynamicFilterListType, ListItemOptionType, RowsFilterType } from '../../types';
import DynamicListItemCascader from './DynamicListItemCascader';

export interface IDynamicList extends SelectProps<any> {
    query: GraphQLTaggedNode;
    fragmentList: GraphQLTaggedNode;
    fragmentConnection: GraphQLTaggedNode;
    fragment: GraphQLTaggedNode;
    nameQuery: string;
    // list of field that we need to see as result in final list. Example: ['name', 'number']
    fields: string[];
    // template that can include only field from props 'fields'. Example: 'name (number)' => 'Mr. Green (234)'
    template?: string;
    filterList?: DynamicFilterListType[];
    disableList?: DynamicFilterListType[];
    indexUpdate?: number;
    environment?: Environment;
    countRecord?: number;
    filterFlexible?: string;
    //key as field name according query and value as value of the field Example: {projectId: '9d61f5a2-34ce-4c9c-b4f1-65877d624c08'}
    // localFilters?: RowsFilterType;
    excludeFilters?: RowsFilterType;
    onChangeWithObject?: (
        value: string | string[],
        option?: DefaultOptionType | DefaultOptionType[],
        selectedItems?: any | any[],
    ) => void;
    onChange?: (value: any, option?: DefaultOptionType) => void;
    renderOptionLabel?: (item: any, label: string) => React.ReactElement;
    disabledList?: Set<string>;
    checkAccessPermission?: (item: any) => boolean;
}

const DynamicList = forwardRef<any, IDynamicList>(
    (
        {
            fields,
            template,
            onChangeWithObject,
            indexUpdate,
            filterList,
            query,
            fragmentList,
            fragmentConnection,
            fragment,
            nameQuery,
            countRecord,
            filterFlexible,
            // localFilters,
            excludeFilters,
            onChange,
            renderOptionLabel,
            disabledList,
            checkAccessPermission,
            ...restProps
        },
        ref,
    ) => {
        const environment = useRelayEnvironment();
        const contextProps = useContext(SmxPropsContext);
        const setterListItem = useDispatchSetterStore<IDynamicListState<any>>(
            getSetterDynamicListItem(contextProps.nameList + contextProps.prefixStore),
        );
        const [render, setRender] = useState(0);
        const [data, setData] = useState<{
            rows: any[];
            count: number;
            connectionId: string;
        }>({ rows: [], count: 0, connectionId: '' });

        const originalItems = useDynamicSelector('dynamicList', 'originalItems');
        const items = useDynamicSelector('dynamicList', 'items');
        const currentItemId = useDynamicSelector('dynamicList', 'currentItemId');
        const spinner = useDynamicSelector('dynamicList', 'spinner');

        const [currentFilterFlexible, setCurrentFilterFlexible] = useState<string>();
        const [isLoadingDataFromServer, setIsLoadingDataFromServer] = useState<boolean>(false);

        const currentSetItem = useRef<Set<string>>(new Set());

        useImperativeHandle(ref, () => ({
            getCurrentItem: () => {
                if (!currentItemId) {
                    return null;
                }

                return getObjectFromRelayStoreById(
                    environment,
                    Array.isArray(currentItemId) ? currentItemId[0] : currentItemId,
                );
            },
        }));

        useEffect(() => {
            const selectedItems = Array.isArray(restProps.value)
                ? restProps.value.map((id) => getObjectFromRelayStoreById(environment, id))
                : [getObjectFromRelayStoreById(environment, restProps.value)];

            console.log(selectedItems);
            setterListItem({
                currentItemId: restProps.value,
                currentItem: selectedItems[0], // ToDo it works but looks strange for mode 'multiple'
            });
        }, [restProps.value]);

        useEffect(() => {
            if (data.rows) {
                setterListItem({
                    originalItems: data.rows
                        // .filter((row) => {
                        //     if (localFilters) {
                        //         const localFilterKeyList = Object.keys(localFilters);
                        //         let keyFilter = true;
                        //
                        //         for (const key of localFilterKeyList) {
                        //             if (Array.isArray(localFilters[key])) {
                        //                 const valueLocalFilter = localFilters[key] as string[];
                        //                 if (valueLocalFilter.length && !valueLocalFilter.includes(`${row[key]}`))
                        //                     keyFilter = false;
                        //             } else if (`${row[key]}` !== localFilters[key]) {
                        //                 keyFilter = false;
                        //             }
                        //         }
                        //
                        //         return keyFilter;
                        //     }
                        //     return true;
                        // })
                        .filter((row) => {
                            if (excludeFilters) {
                                const excludeFilterKeyList = Object.keys(excludeFilters);
                                for (const key of excludeFilterKeyList) {
                                    if (Array.isArray(excludeFilters[key])) {
                                        const valueExcludeFilter = excludeFilters[key] as string[];
                                        if (!valueExcludeFilter.includes(row[key])) return true;
                                    } else if (row[key] !== excludeFilters[key]) {
                                        return true;
                                    }
                                }
                                return false;
                            }
                            return true;
                        })
                        .map((row) => ({
                            ...row,
                            id: row.key,
                        })),
                });
            }
        }, [data.rows, excludeFilters]);

        useEffect(() => {
            setterListItem({
                items: [...originalItems],
            });
        }, [originalItems]);

        const handleChange = (value: string | string[], option?: DefaultOptionType | DefaultOptionType[]) => {
            const selectedItems = Array.isArray(value)
                ? value.map((id) => getObjectFromRelayStoreById(environment, id))
                : [getObjectFromRelayStoreById(environment, value)];

            setterListItem({
                currentItemId: value,
                currentItem: selectedItems[0], // ToDo it works but looks strange for mode 'multiple'
            });

            onChangeWithObject?.(value, option, selectedItems);
            onChange?.(value, option);
            setCurrentFilterFlexible('');
        };

        const customFilterOption = (inputValue: string, option: any) => {
            if ((countRecord && countRecord === data.rows.length) || (!countRecord && data.rows.length === 100)) {
                return true;
            }

            return option.title.toLowerCase().includes(inputValue.toLowerCase());
        };

        const getLabelText = (item: any) => {
            let newTitle = '';
            if (fields.length && template) {
                newTitle = template;
                fields.forEach((field) => {
                    const regex = new RegExp(`\\b${field}\\b`, 'g');
                    const value = (item as any)[field as any] || '';

                    newTitle = newTitle.replace(regex, value);
                });
            } else {
                newTitle = fields.map((field) => (item as any)[field as any]).join(' ');
            }
            return newTitle;
        };

        const renderOption = (item: any): ListItemOptionType => {
            const newTitle = getLabelText(item);

            const accessToItem = checkAccessPermission?.(item);

            const getLabel = () => {
                if (accessToItem === false) {
                    return (
                        <Flex justify={'space-between'}>
                            <span>{newTitle}</span>
                            <LockOutlined />
                        </Flex>
                    );
                }

                return renderOptionLabel?.(item, newTitle) || newTitle;
            };

            return {
                label: getLabel(),
                title: newTitle,
                value: item.id as string,
                disabled: (disabledList && disabledList.has(item.id)) || accessToItem === false,
            };
        };

        const renderLabel = (title: any) => {
            const item = getObjectFromRelayStoreById(environment, title.value);
            currentSetItem.current.add(title.value);
            return <span>{getLabelText(item)}</span>;
        };

        const handleCascaderClick = (e: React.MouseEvent) => {
            e.stopPropagation();
        };

        const popupRender = (menu: React.ReactElement) => (
            <Spin tip="Loading" size="small" spinning={spinner || isLoadingDataFromServer}>
                {!!filterList?.length && (
                    <>
                        <div onClick={handleCascaderClick}>
                            {filterList?.map((filter) => (
                                <SuspenseList key={filter.fieldName}>
                                    <DynamicListItemCascader
                                        fieldName={filter.fieldName}
                                        filters={filter.filters}
                                        indexUpdate={indexUpdate}
                                    />
                                </SuspenseList>
                            ))}
                        </div>
                        <Divider style={{ margin: '8px 0' }} />
                    </>
                )}
                {menu}
            </Spin>
        );

        const dataLoader = (
            filter_flexible?: string | null,
            onUpdateData?: (rows: any[], count: number, connectionId: string) => void,
        ) => (
            <DataLoader
                query={query}
                fragmentList={fragmentList}
                fragment={fragment}
                fragmentConnection={fragmentConnection}
                nameQuery={nameQuery}
                onUpdateData={onUpdateData}
                varRequest={{
                    first: countRecord,
                    filter_flexible,
                }}
            />
        );

        const Fallback: React.FC = () => {
            useEffect(() => {
                setIsLoadingDataFromServer(true);
                return () => {
                    setIsLoadingDataFromServer(false);
                };
            }, []);

            return null;
        };

        const resultRenderOption = useMemo(() => {
            return items.map(renderOption);
        }, [items]);

        const onUpdate = (rows: any[], count: number, connectionId: string) => {
            setData({ rows, count, connectionId });
        };

        const onUpdateRender = () => {
            setTimeout(() => {
                setRender((oldValue) => oldValue + 1);
            }, 500);
        };

        return (
            <>
                {currentFilterFlexible ? (
                    <Suspense fallback={<Fallback />}>
                        {dataLoader(combineFilterFlexible(filterFlexible, currentFilterFlexible), onUpdate)}
                    </Suspense>
                ) : (
                    dataLoader(combineFilterFlexible(filterFlexible, currentFilterFlexible), onUpdate)
                )}
                {!!currentSetItem.current.size && (
                    <Suspense fallback={<Fallback />}>
                        {dataLoader(
                            `[{"or":[{"id":"${Array.from(currentSetItem.current).join('|')}"}]}]`,
                            onUpdateRender,
                        )}
                    </Suspense>
                )}
                <Select
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="select option ..."
                    labelRender={renderLabel}
                    popupRender={popupRender}
                    filterOption={customFilterOption}
                    onSearch={(value) => {
                        if (!value) {
                            setCurrentFilterFlexible('');
                            return;
                        }

                        if (
                            (countRecord && countRecord === data.rows.length) ||
                            (!countRecord && data.rows.length === 100)
                        ) {
                            const fieldsValue = fields.map((fieldName) => `{"${fieldName}":"*${value}"}`);
                            setCurrentFilterFlexible(`[{"or":[${fieldsValue.join(',')}]}]`);
                        }
                    }}
                    options={resultRenderOption}
                    value={currentItemId}
                    onChange={handleChange}
                    loading={spinner}
                    showSearch
                    onOpenChange={() => {
                        setCurrentFilterFlexible('');
                    }}
                    {...restProps}
                />
            </>
        );
    },
);

export default DynamicList;
